import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { FixturePage } from '@cds/betting-offer';
import {
    CrmConfig,
    GridConfig,
    PrettyUrlsConfig,
    ScrollingSizeConfig,
    Sitecore,
    TeamPagesConfig,
    TimeFilterConfig,
} from '@frontend/sports/common/client-config-data-access';
import { CountItem, ItemType, LeagueItem, children } from '@frontend/sports/common/core/data-access/sport-model';
import { CssSupportService } from '@frontend/sports/common/core/utils/css-support';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { HooksWireup, OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { ScrollContainerService } from '@frontend/sports/common/core/utils/scroll-container';
import { ThemeInverse } from '@frontend/sports/common/ui/shared';
import { LinkDirective } from '@frontend/sports/common/ui/sports-themed-pills';
import { SportsThemedTabsDirective } from '@frontend/sports/common/ui/sports-themed-tab-group';
import { GridGrouping, GridSorting, StandingsData, SubscriptionTopic } from '@frontend/sports/grid/core/feature/model';
import { GridOption, ObservableGrid, ObservableGridFactory } from '@frontend/sports/grid/core/feature/observables';
import { GridActions, IGridRootState, getEvents } from '@frontend/sports/grid/core/feature/store';
import { CompetitionRoute } from '@frontend/sports/navigation/core/feature/models';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { MarketOfferUrlParam, SportUrlParam } from '@frontend/sports/navigation/core/feature/url-helper';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { DsTabsModule } from '@frontend/ui/tabsgroup';
import { MediaQueryService } from '@frontend/vanilla/core';
import { SpeculativeLinkDirective } from '@frontend/vanilla/features/speculative-link';
import { Store } from '@ngrx/store';
import { capitalize } from 'lodash-es';
import { Observable, Subject, Subscription, distinctUntilChanged, map, merge, of, skip, takeUntil } from 'rxjs';

import { CalendarService } from '../../calendar/calendar.service';
import { IntervalLink } from '../../calendar/count-interval.service';
import { TimeFilterTrackingService, TimeFrameLocation } from '../../calendar/time-filters-tracking.service';
import { ShowInEpcotDirective } from '../../common/epcot-config.directive';
import { EpcotConfigService, EpcotModule } from '../../common/epcot-config.service';
import { ErrorComponent } from '../../common/error/error.component';
import { HideHeaderService } from '../../common/hide-header.service';
import { Promotion } from '../../crm-offer-data/crm-offer.model';
import { CrmPromotionContainerComponent } from '../../crm-promotion-container/crm-promotion-container.component';
import { CrmPromotionMapperService } from '../../crm-promotion-container/crm-promotion-mapper.service';
import { CrmPromotionProviderService } from '../../crm-promotion/crm-promotion-provider.service';
import { ComponentProxy } from '../../deferred/component-loader.service';
import { ComponentProxyDirective } from '../../deferred/component-proxy.directive';
import { ScrolledToBottomDirective } from '../../directives/scrolled-to-bottom.directive';
import { GridModule } from '../../grid/grid.module';
import { SortSelectorComponent } from '../../grid/header/sort-selector.component';
import {
    HighLightsMarqueeBinding,
    HighlightsMarqueeComponentLoaderService,
} from '../../highlights-marquee/highlights-marquee-component-loader.service';
import { HighlightsMarqueePage } from '../../highlights-marquee/highlights-marquee-page.enum';
import { Tile } from '../../highlights-marquee/marquee-tile.model';
import { MarqueeTileService } from '../../highlights-marquee/marquee-tile.service';
import { ColumnLayoutService } from '../../layout/column-layout.service';
import { LiveNowLinkRegisterService } from '../../live-now-item/live-now-link-register.service';
import { SignpostTypeService } from '../../shared-priceboost/Services/signpost-type.service';
import { SignpostType } from '../../shared-priceboost/signpost.model';
import type { ShowcaseModuleData } from '../../showcase/showcase.model';
import { ShowcaseModule } from '../../showcase/showcase.module';
import { TabBarItem } from '../../tab-bar/tab-bar.models';
import { TeamPagesViewModel } from '../../teampages-core/team-pages.model';
import { TeamPagesCompetitionViewBinding, TeamPagesComponentLoaderService } from '../../teampages/team-pages-component-loader.service';
import { CalendarCompetitionTreeModalDialogService } from './calendar-competition-tree-modal-dialog.service';
import { CompetitionGridActions } from './competitions/competition-grid.actions';
import { CompetitionGridHelper } from './competitions/competition-grid.helper';
import { FixtureList, FixtureTab } from './competitions/competition.models';
import { FixtureListHeaderComponent } from './fixture-list-header.component';
import { FixtureListMenuComponent } from './fixture-list-menu.component';
import { EventListService } from './services/event-list.service';
import { SortingStateService } from './services/sorting-state.service';
import { StandingsComponent } from './standings.component';
import { StandingsService } from './standings.service';
import { TournamentGroupsComponent } from './tournament-groups.component';
import { VirtualCompetitionModel, VirtualCompetitionService } from './virtual-competition.service';

export enum FixtureView {
    Grid,
    GridList,
    Standings,
    Teams,
    PriceBoost,
}

@HooksWireup()
@Component({
    imports: [
        CommonModule,
        ComponentProxyDirective,
        CrmPromotionContainerComponent,
        DsTabsModule,
        ErrorComponent,
        FixtureListHeaderComponent,
        FixtureListMenuComponent,
        GridModule,
        LinkDirective,
        ScrolledToBottomDirective,
        ShowInEpcotDirective,
        ShowcaseModule,
        SortSelectorComponent,
        SpeculativeLinkDirective,
        SportsThemedTabsDirective,
        StandingsComponent,
        TournamentGroupsComponent,
    ],
    providers: [ObservableGridFactory, SignpostTypeService, CalendarCompetitionTreeModalDialogService],
    selector: 'ms-fixture-list',
    templateUrl: 'fixture-list.component.html',
})
export class FixtureListComponent extends OnRouteResolve<FixtureList> implements OnDestroy, OnInit {
    grid$: ObservableGrid;
    gridId: string;
    gridOptions: GridOption;
    error?: string;
    sport?: CountItem;
    params: CompetitionRoute;
    isOutrightMarket: boolean;
    virtualCompetitionModel: VirtualCompetitionModel | null;
    virtualCompetitionSiblings: number[] | null;
    page = HighlightsMarqueePage.Competition;
    navigation?: TabBarItem[];
    viewType = FixtureView;
    view?: FixtureView;
    teampagesViewModel: TeamPagesViewModel | undefined;
    teamPagesCompetitionComponent: ComponentProxy<TeamPagesCompetitionViewBinding>;
    tracking = '';
    readonly timeFrameLocation = TimeFrameLocation.CalendarPage;
    loading = false;
    eventsTotalCount = 0;
    standingsData: StandingsData = {
        groups: [],
    };

    overviewSport: CountItem | undefined;
    tiles: (Tile | Promotion)[];
    allMarquees: Tile[];
    allPromotions: Promotion[] = [];
    crmPromotions: Promotion[] = [];

    showcaseModuleData?: ShowcaseModuleData;
    showBigEventModule = false;
    private cleanup$: Observable<any>;
    competitionTitle: string;
    isTouchActionSupported = true;
    calendarIntervals?: IntervalLink[];
    competitions: TabBarItem[] | undefined = [];
    routerEventsSubscription: Subscription;
    isThemeEpcot: boolean;
    module = EpcotModule.BetGenerator; //change the modulename
    marqueeComponent: ComponentProxy<HighLightsMarqueeBinding>;
    trackCalenderTitle?: string;
    mediaQuery$: Observable<boolean>;
    themeInverseIndicator: ThemeInverse;
    private destroyed$ = new Subject<void>();
    shouldHideAllMarketsLink: boolean;

    constructor(
        public sitecore: Sitecore,
        private eventService: EventListService,
        private gridFactory: ObservableGridFactory,
        private calendar: CalendarService,
        private redirectHelper: RedirectHelperService,
        private gridConfig: GridConfig,
        private gridHelper: CompetitionGridHelper,
        private urlConfig: PrettyUrlsConfig,
        private sortingStateService: SortingStateService,
        private columnLayout: ColumnLayoutService,
        private crmPromotionProvider: CrmPromotionProviderService,
        private crmPromotionMapper: CrmPromotionMapperService,
        private crmConfig: CrmConfig,
        private marqueeTileService: MarqueeTileService,
        route: ActivatedRoute,
        private standingService: StandingsService,
        private virtualCompetitionService: VirtualCompetitionService,
        private store: Store<IGridRootState>,
        private cssSupportService: CssSupportService,
        private teamPagesConfig: TeamPagesConfig,
        private loader: TeamPagesComponentLoaderService,
        private timeFilterConfig: TimeFilterConfig,
        private competitionTreeModalDialogService: CalendarCompetitionTreeModalDialogService,
        private liveButton: LiveNowLinkRegisterService,
        private routerEventsService: RouterEventsService,
        private epcotConfigService: EpcotConfigService,
        private signpostTypeService: SignpostTypeService,
        private marqueeLoader: HighlightsMarqueeComponentLoaderService,
        private scrollingSizeConfig: ScrollingSizeConfig,
        private scrollContainer: ScrollContainerService,
        private hideHeaderService: HideHeaderService,
        private mediaQuery: MediaQueryService,
        private timeFilterTrackingService: TimeFilterTrackingService,
        private redirect: RedirectHelperService,
        private trackerService: TrackingService,
    ) {
        super(route);

        this.teamPagesCompetitionComponent = this.loader.getTeamPagesComponent();
        this.cleanup$ = merge(this.destroyed$, this.route.data.pipe(skip(1)));

        this.isTouchActionSupported = this.cssSupportService.isTouchActionSupported();
        this.marqueeComponent = this.marqueeLoader.getHighlightsMarqueeComponent();
        this.mediaQuery$ = this.mediaQuery.observe().pipe(
            map(() => this.mediaQuery.isActive('lt-md')),
            distinctUntilChanged(),
        );
        const isInverseActive = toSignal(this.mediaQuery$, { requireSync: true });
        this.themeInverseIndicator = {
            'mgm-4': { isInverse: isInverseActive() },
        };
    }

    ngOnInit(): void {
        if (this.scrollingSizeConfig.hideHeaderOnScroll.includes(this.page)) {
            this.scrollContainer.scroll.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
                this.hideHeaderService.onScroll();
            });
        }
    }

    onRouteResolved(model: FixtureList): void {
        this.eventsTotalCount = 0;
        this.setModel(model);
        const headerData = this.params.dynamicOfferCategory === undefined ? this.params.marketOffer : this.params.dynamicOfferCategory;
        this.isOutrightMarket = headerData === MarketOfferUrlParam.Outrights;

        this.routerEventsSubscription?.unsubscribe();
        if (
            this.params?.context === this.urlConfig.translations.betting ||
            this.params?.context === this.urlConfig.translations.conferences ||
            (model.params?.context === 'favourites-list' && model.params?.league)
        ) {
            this.crmPromotionProvider.promotions$.pipe(takeUntil(this.cleanup$)).subscribe((promos) => this.promotionChanged(promos));
        }

        this.getVirtualCompetition(model.sport, model.virtualCompetition)
            .pipe(takeUntil(this.cleanup$))
            .subscribe((virtualCompetitionModel) => {
                this.virtualCompetitionModel = virtualCompetitionModel;

                const options = { ...this.gridOptions, disableGroupSorting: !this.isSortingVisible() };

                if (!this.hasEvents(model)) {
                    return;
                }

                this.grid$ = this.gridFactory.fromResponse(this.gridId, model.fixtures, options);
            });

        this.allMarquees = model.marqueeData || [];
        this.tiles = this.marqueeTileService.combineWithPromotions(this.allMarquees, this.allPromotions);
        this.overviewSport = model.sportsOverview;
        this.competitions = model.calendarPills;

        this.routerEventsSubscription = this.routerEventsService.currentActivationEnd.pipe(takeUntil(this.cleanup$)).subscribe(() => {
            if (this.showLiveLink()) {
                this.liveButton.addLiveButton(model.calendarLiveCounts);
            }
        });
        this.trackCalenderTitle = model.calendarIntervals?.find((interval) => interval.active)?.trackingSource;
        this.setIsThemeEpcot();
        this.updateMarqueeData();
    }

    ngOnDestroy(): void {
        this.liveButton.removeLiveButton();
        this.hideHeaderService.removeHideHeader();
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    loadMore(): void {
        if (!this.grid$ || !this.hasMore || this.loading) {
            return;
        }
        this.getEvents();
    }

    get isFavouriteVisible(): boolean {
        return !this.virtualCompetitionModel || !!this.virtualCompetitionModel.realCompetitionId;
    }

    competitionTreePopup() {
        this.competitionTreeModalDialogService.openDialog(this.overviewSport, this.params);
    }

    goBack(): void {
        this.redirectHelper.goBack();
    }

    onSelectedTab(name: string): void {
        const activeCalenderTab = this.calendarIntervals?.find((tab) => {
            return tab.name === name;
        });

        if (activeCalenderTab) {
            this.redirect.goToPage(activeCalenderTab.url);
            this.trackerService.update({ [trackingConstants.PAGE_REFERRING_ACTION]: this.tracking });
            if (activeCalenderTab.url?.startsWith('http')) {
                this.trackerService.triggerEventWithCleaning();
            }
            this.timeFilterTrackingService.trackingCalendarNavigation({
                positionEvent: activeCalenderTab.title,
                locationEvent: this.timeFrameLocation,
                urlClicked: activeCalenderTab.url,
                contentPosition: this.calendarIntervals?.indexOf(activeCalenderTab),
            });
        }
    }

    get activeCalender(): string {
        return this.calendarIntervals?.find((tab) => !!tab.active)?.name ?? '';
    }

    private promotionChanged(model: Promotion[]): void {
        this.allPromotions = model;

        const competitionIds = this.virtualCompetitionSiblings || this.getCompetitionIds();
        this.crmPromotions = this.crmPromotionMapper
            .getByCompetition(model, competitionIds)
            .slice(0, this.crmConfig.promotions.bannerCount.competitionLobby);

        this.tiles = this.marqueeTileService.combineWithPromotions(this.allMarquees, this.allPromotions);
        this.updateMarqueeData();
    }

    private updateMarqueeData() {
        this.marqueeComponent.update({
            tiles: this.tiles,
            page: this.page,
        });
    }

    private getCompetitionIds(): number[] {
        if (this.params.league) {
            const leagues = (Array.isArray(this.params.league) ? this.params.league : [this.params.league]).filter(isDefined);
            if (leagues.length) {
                return leagues;
            }
        }

        if (this.params.region) {
            return this.getLeagueIdsFromRegion();
        }

        if (this.params.subContext === SportUrlParam.Competitions && this.grid$) {
            return this.grid$.getState().groups.map((group) => group.id);
        }

        return [];
    }

    private getLeagueIdsFromRegion(): number[] {
        if (!this.sport) {
            return [];
        }

        if (!this.params.region) {
            return [];
        }

        return children(this.sport, ItemType.Competition).map((l) => l.id);
    }

    private setModel(data: FixtureList): void {
        this.gridId = this.gridHelper.getId(data.params);
        this.params = data.params || {};
        this.tracking = this.getTracking(data, this.params);
        this.navigation = data.tabs;
        this.view = this.getView(this.params);
        this.sport = data.sport;
        this.virtualCompetitionSiblings = data.virtualCompetition ? data.virtualCompetition.siblings : null;
        this.standingsData = this.getStandings(data.sport, this.params.virtualCompetitionGroup, data.virtualCompetition);

        const hasMarketGrouping = this.params.marketCategory != null || this.view === FixtureView.GridList;

        // markets with dynamic categories have specials category set in order to be displayed in this page
        const hasSpecialsCategory = !!this.params.dynamicOfferCategory && this.params.marketOffer === 'specials';
        this.gridOptions = {
            activeGrouping:
                this.params.marketCategory !== null && this.params.marketCategory !== undefined
                    ? this.params.marketCategory
                    : this.params.marketTemplate,
            marketGrouping: hasMarketGrouping,
            moreGrouping:
                (this.params.context === this.urlConfig.translations.betting || this.params.context === this.urlConfig.translations.conferences) &&
                !!this.sport &&
                this.sport.counts.nonGridable > 0,
            collapsedThreshold: this.gridConfig.collapseSize,
            subscriptionTopic: this.getTopic(hasMarketGrouping, hasSpecialsCategory),
        };
        this.teampagesViewModel = data.teampagesViewModel;
        this.teamPagesCompetitionComponent.update({ teampagesViewModel: data.teampagesViewModel });
        this.competitionTitle = data.competitionTitle || '';
        this.calendarIntervals = data.calendarIntervals;
        this.setGrouping(this.params.context, data.sorting, data.totalCompetitions);
        this.setSorting(data.sorting, data.totalCompetitions, this.params.virtualCompetitionGroup);
        this.setView(data, this.params);
        this.setShowcase(data);
        this.shouldHideAllMarketsLink = this.params?.context === this.urlConfig.translations.betting;
    }

    private setGrouping(routeContext?: string, sorting?: GridSorting, totalCompetitionCount?: number): void {
        if (totalCompetitionCount && totalCompetitionCount > 1) {
            this.gridOptions.grouping = sorting === GridSorting.Time ? GridGrouping.Date : GridGrouping.League;

            return;
        }

        if (this.calendar.isInterval(routeContext)) {
            this.gridOptions.grouping = GridGrouping.League;

            return;
        }

        this.gridOptions.grouping = GridGrouping.Date;
    }

    private setSorting(sorting?: GridSorting, totalCompetitionCount?: number, virtualGroupCompetitionId?: number): void {
        if (totalCompetitionCount && totalCompetitionCount > 1 && !virtualGroupCompetitionId) {
            this.gridOptions.sorting = sorting;

            return;
        }

        this.gridOptions.sorting = undefined;
    }

    private setView(page: FixturePage, route: CompetitionRoute): void {
        this.view = this.getView(route);

        if (this.grid$) {
            let sorting;

            if (route.marketOffer !== MarketOfferUrlParam.Outrights && route.marketOffer !== MarketOfferUrlParam.Specials) {
                if (route.isVirtual) {
                    sorting = this.sortingStateService.getVirtualCompetition();
                } else {
                    sorting = this.sortingStateService.get('competitions', route.sport) || GridSorting.Competition;
                }
            }

            this.setGrouping(route.context, sorting, page.totalCompetitions);
            this.setSorting(sorting, page.totalCompetitions, route.virtualCompetitionGroup);
            this.grid$.destroy();

            if (this.view === FixtureView.Standings) {
                return;
            }
        }

        this.setError(page, route);
        this.eventsTotalCount = page.totalCount;

        if (!this.hasEvents(page)) {
            return;
        }

        this.tracking = this.updateSourceOfBet(page, route);
    }

    private updateSourceOfBet(data: FixturePage, route: CompetitionRoute): string {
        if (this.navigation) {
            const activeTab = this.navigation.find((tab) => !!tab.active);
            if (activeTab) {
                return `${this.getTracking(data, route)}/${FixtureTab[activeTab.id]}`;
            }
        }

        return this.tracking;
    }

    get showEmptySpace(): boolean {
        return this.columnLayout.layout.shouldHaveRightColumn && this.hasMore;
    }

    get hasMore(): boolean {
        return this.eventsTotalCount > this.eventCount;
    }

    get eventCount(): number {
        if (!this.grid$) {
            return 0;
        }
        const grid = this.grid$.getState();

        if (!grid) {
            return 0;
        }

        return getEvents(grid).length;
    }

    private getEvents(): void {
        this.loading = true;

        let sorting = this.grid$?.getState().sorting;

        if (sorting === undefined) {
            sorting = this.params?.isVirtual ? GridSorting.Time : GridSorting.Competition;
        }

        this.eventService.getFixtureList(this.params, sorting, this.eventCount, this.gridConfig.pageSize).subscribe((result) => {
            if (this.hasEvents(result)) {
                this.addEvents(result);
            }

            this.loading = false;
        });
    }

    private getTracking(page: FixturePage, routeData: CompetitionRoute): string {
        let routeContext = routeData.context;

        if (routeData.isVirtual) {
            let groupName = routeData.virtualCompetitionGroupName;

            if (!groupName) {
                groupName = 'All';
            }

            if (routeContext && this.showTimeFilters(routeContext)) {
                return `EventList/League/Soon/${groupName}`;
            }

            return `EventList/League/${groupName}`;
        }

        if (this.hasEvents(page) && routeData.league) {
            if (routeContext && this.showTimeFilters(routeContext)) {
                return 'EventList/League/Soon';
            }

            return 'EventList/League';
        }

        if (routeContext) {
            if (this.calendar.isInterval(routeContext)) {
                routeContext = 'soon';
            }

            if (routeContext === 'live-calendar') {
                routeContext = 'live';
            }
        }

        return `EventList/${capitalize(routeContext || 'all')}`;
    }

    private setError(page: FixturePage, route: CompetitionRoute): void {
        if (this.hasEvents(page)) {
            this.error = undefined;
        } else if (this.calendarIntervals?.length) {
            this.error = this.sitecore.event.CalendarNoEventsErrMessage;
        } else {
            if (route.league) {
                this.error = this.sitecore.event.ErrNoEventsPerLeague;
            } else if (route.region) {
                this.error = this.sitecore.event.ErrNoEventsPerRegion;
            } else if (route.context) {
                this.error = this.sitecore.event.ErrNoEventsPerFilter;
            } else if (route.sport) {
                this.error = this.sitecore.event.ErrNoEventsPerSport;
            } else {
                this.error = this.sitecore.event.ErrNoEvents;
            }
        }
    }

    private getView(route: CompetitionRoute): FixtureView {
        const tab = this.navigation ? this.navigation.find((offer) => !!offer.active) : undefined;
        const tabId = tab?.id || FixtureTab[capitalize(route.marketOffer) as keyof typeof FixtureTab];

        if (!route.marketTemplate) {
            if (!tabId) {
                return FixtureView.Grid;
            } else if (tabId === FixtureTab.Standings) {
                return FixtureView.Standings;
            } else if (tabId === FixtureTab.Matches) {
                this.signpostTypeService.signpostType = tab ? SignpostType.PriceBoost : SignpostType.None;

                return FixtureView.Grid;
            } else if (tabId === FixtureTab.Teams && this.teamPagesConfig.isEnabled && route.context !== this.urlConfig.translations.bets) {
                return FixtureView.Teams;
            } else if (tabId === FixtureTab.Priceboost) {
                return FixtureView.PriceBoost;
            }
        }

        return FixtureView.GridList;
    }

    private getTopic(hasMarketGrouping: boolean, fallbackToSpecials: boolean): SubscriptionTopic | undefined {
        if (this.navigation) {
            const active = this.navigation.find((offer) => !!offer.active);

            if (!active) {
                return;
            }

            if (active.id === FixtureTab.Specials) {
                return SubscriptionTopic.Specials;
            } else if (active.id === FixtureTab.Outrights) {
                return SubscriptionTopic.Outrights;
            }
        }

        return hasMarketGrouping && fallbackToSpecials ? SubscriptionTopic.Specials : undefined;
    }

    private hasEvents(page?: FixturePage): page is FixturePage & boolean {
        return !!page?.fixtures?.length;
    }

    private addEvents(page: FixturePage): void {
        const grid = this.grid$.getState();
        this.store.dispatch(CompetitionGridActions.addEvents({ payload: { id: grid.id, fixtures: page.fixtures } }));
    }

    private getStandings(sport: CountItem | undefined, selectedVirtualGroupId?: number, selectedVirtualCompetition?: LeagueItem): StandingsData {
        if (this.view === FixtureView.Standings && sport) {
            const groups = this.standingService.getStandingsGroups({ sport, selectedVirtualGroupId });

            return {
                groups,
                virtualCompetitionId: selectedVirtualCompetition?.id,
            };
        }

        return {
            groups: [],
        };
    }

    private setShowcase(data: FixtureList): void {
        this.showcaseModuleData = data.showcaseData;

        this.showBigEventModule = !!this.showcaseModuleData?.events?.length;
    }

    private getVirtualCompetition(
        sport: CountItem | undefined,
        virtualCompetition: LeagueItem | undefined,
    ): Observable<VirtualCompetitionModel | null> {
        if (!sport || !virtualCompetition) {
            return of(null);
        }

        return this.virtualCompetitionService.subscribe$(sport, virtualCompetition);
    }

    private isSortingVisible(): boolean {
        return !this.virtualCompetitionModel?.groups?.some((group) => !group.canBeFavourited);
    }

    private showLiveLink(): boolean {
        if (!this.params?.context) {
            return false;
        }

        return this.showTimeFilters(this.params.context);
    }

    private showTimeFilters(context: string): boolean {
        return !!(this.timeFilterConfig.isTimeFilterEnabled && this.calendar.getInterval(context));
    }

    sortingChanged(sorting: GridSorting, id: string): void {
        this.store.dispatch(GridActions.changeSorting({ payload: { id, sorting } }));
    }

    private setIsThemeEpcot(): void {
        this.isThemeEpcot = this.epcotConfigService.isEnabled();
    }
}
